import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Home,
  Shirt,
  ChevronRight,
  Package,
  Plus,
  Minus,
  Check,
  X,
  ArrowLeft,
  Search,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchEdificios } from '@/pages/parametrizacion/edificios/Services/edificios.services';
import { fetchHospedajes } from '@/services/hospedajes.service';
import { registrarRecoleccion } from '@/services/movimientosLenceria.service';
import { fetchSolicitudesVerticalesPendientes, completarSolicitudesPorEdificio } from '@/services/solicitudes.service';
import { fetchGarmentTypesByEdificioId } from '@/services/garmentTypes.service';
import ModalResumenRecoleccion from './ModalResumenRecoleccion';

const RecoleccionRopa = ({ onRecoleccionExitosa }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Estados de navegación
  const [step, setStep] = useState('edificios'); // 'edificios', 'hospedajes', 'prendas'
  const [selectedEdificio, setSelectedEdificio] = useState(null);
  const [selectedHospedaje, setSelectedHospedaje] = useState(null);
  
  // Datos
  const [edificios, setEdificios] = useState([]);
  const [edificiosConSolicitud, setEdificiosConSolicitud] = useState([]); // IDs de edificios con solicitudes pendientes
  const [hospedajes, setHospedajes] = useState([]);
  const [prendas, setPrendas] = useState([]);
  const [prendasRecolectadas, setPrendasRecolectadas] = useState({});
  
  // Estados de carga
  const [isLoadingEdificios, setIsLoadingEdificios] = useState(false);
  const [isLoadingHospedajes, setIsLoadingHospedajes] = useState(false);
  const [isLoadingPrendas, setIsLoadingPrendas] = useState(false);
  
  // Modal de resumen
  const [showResumenModal, setShowResumenModal] = useState(false);
  const [prendasParaResumen, setPrendasParaResumen] = useState([]);
  const [recoleccionSinConfirmar, setRecoleccionSinConfirmar] = useState(false);
  
  // Búsqueda de prendas
  const [searchTerm, setSearchTerm] = useState('');
  
  // Búsqueda de edificios
  const [searchEdificio, setSearchEdificio] = useState('');

  // Cargar edificios y solicitudes al montar
  useEffect(() => {
    loadEdificios();
    loadSolicitudesPendientes();
  }, []);

  const loadSolicitudesPendientes = async () => {
    try {
      const response = await fetchSolicitudesVerticalesPendientes();
      
      // Extraer IDs únicos de edificios con solicitudes pendientes
      const edificioIds = [...new Set(
        (response.solicitudes || []).map(sol => sol.edificio?.id).filter(Boolean)
      )];
      
      setEdificiosConSolicitud(edificioIds);
    } catch (error) {
      console.error('Error al cargar solicitudes pendientes:', error);
      setEdificiosConSolicitud([]);
    }
  };

  const loadEdificios = async () => {
    setIsLoadingEdificios(true);
    try {
      const response = await fetchEdificios({
        limit: 1000,
        offset: 1, // Backend espera offset basado en páginas (1, 2, 3...)
        query: { estado: 'Activo' },
        userId: null,
        userRole: null
      });
      
      // Verificar si hay error en la respuesta
      if (response && response.state === false) {
        throw new Error(response.message || 'Error al cargar edificios');
      }
      
      if (response && response.data) {
        // Asegurar que siempre sea un array
        const edificiosData = Array.isArray(response.data) ? response.data : [];
        setEdificios(edificiosData);
      } else {
        setEdificios([]);
      }
    } catch (error) {
      console.error('Error al cargar edificios:', error);
      setEdificios([]);
      toast({
        title: 'Error',
        description: error.message || 'No se pudieron cargar los edificios',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingEdificios(false);
    }
  };

  const handleSelectEdificio = async (edificio) => {
    setSelectedEdificio(edificio);
    
    if (edificio.tipoLogistica === 'vertical') {
      // Edificio vertical: cargar prendas directamente
      setStep('prendas');
      await loadPrendasVertical(edificio);
    } else {
      // Edificio horizontal: cargar hospedajes
      setStep('hospedajes');
      await loadHospedajes(edificio);
    }
  };

  const loadHospedajes = async (edificio) => {
    setIsLoadingHospedajes(true);
    try {
      const response = await fetchHospedajes({
        limit: 1000,
        offset: 1, // Backend espera offset basado en páginas (1, 2, 3...)
        query: {
          'edificio.id': edificio._id,
          estado: 'Activo',
        }
      });
      
      if (response && response.data) {
        const hospedajesData = Array.isArray(response.data) ? response.data : [];
        setHospedajes(hospedajesData);
      } else {
        setHospedajes([]);
      }
    } catch (error) {
      console.error('Error al cargar hospedajes:', error);
      setHospedajes([]);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los hospedajes',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingHospedajes(false);
    }
  };

  const loadPrendasVertical = async (edificio) => {
    setIsLoadingPrendas(true);
    try {
      // Cargar tipos de prenda asignados al edificio usando el servicio
      const response = await fetchGarmentTypesByEdificioId(edificio._id);
      
      let prendasData = Array.isArray(response.data) ? response.data : [];
      
      // Ordenar prendas alfabéticamente por nombre
      prendasData = prendasData.sort((a, b) => {
        const nombreA = (a.nombre || '').toLowerCase();
        const nombreB = (b.nombre || '').toLowerCase();
        return nombreA.localeCompare(nombreB);
      });
      
      setPrendas(prendasData);
      
      // Cargar solicitudes pendientes del edificio para auto-llenar cantidades
      const solicitudesResponse = await fetchSolicitudesVerticalesPendientes();
      const solicitudesEdificio = (solicitudesResponse.solicitudes || []).filter(
        sol => sol.edificio?.id === edificio._id
      );
      
      // Crear mapa de cantidades solicitadas por prenda
      const cantidadesSolicitadas = {};
      solicitudesEdificio.forEach(solicitud => {
        if (solicitud.items && Array.isArray(solicitud.items)) {
          solicitud.items.forEach(item => {
            const prendaId = item.id;
            if (prendaId) {
              cantidadesSolicitadas[prendaId] = (cantidadesSolicitadas[prendaId] || 0) + (item.cantidad || 0);
            }
          });
        }
      });
      
      // Inicializar cantidades: usar cantidades solicitadas si existen, sino 0
      const initial = {};
      prendasData.forEach(prenda => {
        const id = prenda.prendaId || prenda._id;
        if (id) {
          initial[id] = cantidadesSolicitadas[id] || 0;
        }
      });
      setPrendasRecolectadas(initial);
      
      // Mostrar mensaje si se auto-llenaron cantidades
      const totalAutoLlenado = Object.values(cantidadesSolicitadas).reduce((sum, cant) => sum + cant, 0);
      if (totalAutoLlenado > 0) {
        toast({
          title: 'Cantidades auto-llenadas',
          description: `Se cargaron ${totalAutoLlenado} prendas desde ${solicitudesEdificio.length} solicitud(es) pendiente(s)`,
        });
      }
    } catch (error) {
      console.error('Error al cargar prendas:', error);
      setPrendas([]);
      setPrendasRecolectadas({});
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las prendas',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingPrendas(false);
    }
  };

  const handleSelectHospedaje = async (hospedaje) => {
    setSelectedHospedaje(hospedaje);
    setStep('prendas');
    await loadPrendasHorizontal(hospedaje);
  };

  const loadPrendasHorizontal = async (hospedaje) => {
    setIsLoadingPrendas(true);
    try {
      // Usar el nuevo endpoint que obtiene todas las prendas únicas
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${API_URL}/hospedajes/${hospedaje._id}/prendas`);
      
      if (!response.ok) throw new Error('Error al cargar prendas');
      
      const data = await response.json();
      let prendasData = Array.isArray(data.data) ? data.data : [];
      
      // Ordenar prendas alfabéticamente por nombre
      prendasData = prendasData.sort((a, b) => {
        const nombreA = (a.nombre || '').toLowerCase();
        const nombreB = (b.nombre || '').toLowerCase();
        return nombreA.localeCompare(nombreB);
      });
      
      setPrendas(prendasData);
      
      // Inicializar cantidades en 0
      const initial = {};
      prendasData.forEach(prenda => {
        const id = prenda.prendaId || prenda._id;
        if (id) {
          initial[id] = 0;
        }
      });
      setPrendasRecolectadas(initial);
    } catch (error) {
      console.error('Error al cargar prendas:', error);
      setPrendas([]);
      setPrendasRecolectadas({});
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las prendas',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingPrendas(false);
    }
  };

  const handleChangeCantidad = (prendaId, delta) => {
    setPrendasRecolectadas(prev => {
      const newValue = (prev[prendaId] || 0) + delta;
      return {
        ...prev,
        [prendaId]: Math.max(0, newValue)
      };
    });
  };

  const handleInputCantidad = (prendaId, value) => {
    const numValue = parseInt(value) || 0;
    setPrendasRecolectadas(prev => ({
      ...prev,
      [prendaId]: Math.max(0, numValue)
    }));
  };

  const handleConfirmarRecoleccion = (sinConfirmar = false) => {
    const prendasConCantidad = Object.entries(prendasRecolectadas)
      .filter(([_, cantidad]) => cantidad > 0)
      .map(([prendaId, cantidad]) => {
        const prenda = prendas.find(p => (p.prendaId || p._id) === prendaId);
        return {
          prendaId,
          nombre: prenda?.nombre || '',
          cantidad,
          peso: prenda?.peso || 0
        };
      });
    
    if (prendasConCantidad.length === 0) {
      toast({
        title: 'Advertencia',
        description: 'Debe recolectar al menos una prenda',
        variant: 'destructive',
      });
      return;
    }
    
    // Abrir modal de resumen
    setPrendasParaResumen(prendasConCantidad);
    setShowResumenModal(true);
    setRecoleccionSinConfirmar(sinConfirmar);
  };

  const handleConfirmarConFirma = async (datosAdicionales) => {
    try {
      // Preparar datos para el backend
      const recoleccionData = {
        edificio: {
          id: selectedEdificio._id,
          nombre: selectedEdificio.nombre,
          ciudad: selectedEdificio.ciudad,
          tipoLogistica: selectedEdificio.tipoLogistica
        },
        hospedaje: selectedHospedaje ? {
          id: selectedHospedaje._id,
          nombrePropiedad: selectedHospedaje.nombrePropiedad
        } : null,
        prendas: prendasParaResumen,
        receptor_nombre: datosAdicionales.receptor_nombre,
        observaciones: datosAdicionales.observaciones || '',
        firma: datosAdicionales.firma,
        peso_real: datosAdicionales.peso_real, // Peso real total (gramos)
        conductor: {
          id: user.id,
          nombre: user.name
        },
        sinConfirmar: recoleccionSinConfirmar
      };

      const response = await registrarRecoleccion(recoleccionData);
      
      // Si es edificio vertical, completar todas las solicitudes pendientes
      if (selectedEdificio.tipoLogistica === 'vertical') {
        try {
          await completarSolicitudesPorEdificio(selectedEdificio._id, {
            recolectadoPor: {
              id: user.id,
              nombre: user.name
            },
            fechaRecoleccion: new Date()
          });
          
          // Recargar solicitudes pendientes para actualizar la lista
          await loadSolicitudesPendientes();
        } catch (solicitudError) {
          console.error('Error al completar solicitudes:', solicitudError);
          // No mostramos error al usuario, la recolección ya se guardó exitosamente
        }
      }
      
      const mensajeEstado = recoleccionSinConfirmar 
        ? 'Pendiente de confirmación en lavandería'
        : 'Redirigiendo a entregas recolectadas...';
      
      toast({
        title: '✅ Recolección Registrada',
        description: `${response.data.consecutivo_recoleccion || response.data.consecutivo} - ${mensajeEstado}`,
      });
      
      // Cerrar modal y resetear
      setShowResumenModal(false);
      setPrendasParaResumen([]);
      setRecoleccionSinConfirmar(false);
      handleVolver();
      
      // Llamar al callback para redirigir al tab de recolectadas (solo si se confirmó)
      if (onRecoleccionExitosa && !recoleccionSinConfirmar) {
        // Usar setTimeout para dar tiempo a que se cierre el modal y se vea el toast
        setTimeout(() => {
          onRecoleccionExitosa();
        }, 500);
      }

      // Devolver los datos guardados para que el modal imprima el ticket
      // (con el consecutivo generado por el backend)
      return response.data;
    } catch (error) {
      console.error('Error al guardar recolección:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la recolección',
        variant: 'destructive',
      });
    }
  };

  const handleVolver = () => {
    if (step === 'prendas') {
      if (selectedEdificio?.tipoLogistica === 'horizontal') {
        setStep('hospedajes');
        setSelectedHospedaje(null);
        setPrendas([]);
        setPrendasRecolectadas({});
        setSearchTerm(''); // Limpiar búsqueda
      } else {
        setStep('edificios');
        setSelectedEdificio(null);
        setPrendas([]);
        setPrendasRecolectadas({});
        setSearchTerm(''); // Limpiar búsqueda
      }
    } else if (step === 'hospedajes') {
      setStep('edificios');
      setSelectedEdificio(null);
      setHospedajes([]);
    }
  };

  const getTotalPrendas = () => {
    return Object.values(prendasRecolectadas).reduce((sum, cant) => sum + cant, 0);
  };

  // Renderizar lista de edificios
  const renderEdificios = () => {
    // Filtrar edificios según búsqueda
    const edificiosFiltrados = edificios.filter(edificio => {
      if (!searchEdificio) return true;
      const nombre = (edificio.nombre || '').toLowerCase();
      const ciudad = (edificio.ciudad || '').toLowerCase();
      const search = searchEdificio.toLowerCase();
      return nombre.includes(search) || ciudad.includes(search);
    });
    
    // Separar edificios con y sin solicitudes
    const edificiosConSol = edificiosFiltrados.filter(ed => edificiosConSolicitud.includes(ed._id));
    const edificiosSinSol = edificiosFiltrados.filter(ed => !edificiosConSolicitud.includes(ed._id));
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Seleccionar Edificio</h3>
          <Badge variant="secondary">{edificios.length} edificios</Badge>
        </div>
        
        {/* Buscador de edificios */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar edificio por nombre o ciudad..."
            value={searchEdificio}
            onChange={(e) => setSearchEdificio(e.target.value)}
            className="pl-10"
          />
          {searchEdificio && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchEdificio('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {isLoadingEdificios ? (
          <div className="text-center py-8 text-gray-500">
            Cargando edificios...
          </div>
        ) : edificios.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No hay edificios disponibles
          </div>
        ) : edificiosFiltrados.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No se encontraron edificios con "{searchEdificio}"
          </div>
        ) : (
          <div className="space-y-4">
            {/* Edificios con solicitudes pendientes */}
            {edificiosConSol.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="bg-orange-500 hover:bg-orange-600">
                    Solicitudes Pendientes ({edificiosConSol.length})
                  </Badge>
                </div>
                {edificiosConSol.map((edificio) => (
                  <motion.div
                    key={edificio._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      className="cursor-pointer hover:shadow-md transition-shadow border-2 border-orange-400 bg-orange-50"
                      onClick={() => handleSelectEdificio(edificio)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center ring-2 ring-orange-400">
                              <Building2 className="h-5 w-5 text-orange-600" />
                            </div>
                            <div>
                              <div className="font-semibold text-orange-900">{edificio.nombre}</div>
                              <div className="text-sm text-orange-700">{edificio.ciudad}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={edificio.tipoLogistica === 'vertical' ? 'default' : 'secondary'} className="bg-orange-600">
                              {edificio.tipoLogistica}
                            </Badge>
                            <ChevronRight className="h-5 w-5 text-orange-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
            
            {/* Edificios sin solicitudes */}
            {edificiosSinSol.length > 0 && (
              <div className="space-y-2">
                {edificiosConSol.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      Otros Edificios ({edificiosSinSol.length})
                    </Badge>
                  </div>
                )}
                {edificiosSinSol.map((edificio) => (
                  <motion.div
                    key={edificio._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleSelectEdificio(edificio)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-semibold">{edificio.nombre}</div>
                              <div className="text-sm text-gray-500">{edificio.ciudad}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={edificio.tipoLogistica === 'vertical' ? 'default' : 'secondary'}>
                              {edificio.tipoLogistica}
                            </Badge>
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Renderizar lista de hospedajes
  const renderHospedajes = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleVolver}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">Seleccionar Hospedaje</h3>
          <p className="text-sm text-gray-500">{selectedEdificio?.nombre}</p>
        </div>
        <Badge variant="secondary">{hospedajes.length} hospedajes</Badge>
      </div>
      
      {isLoadingHospedajes ? (
        <div className="text-center py-8 text-gray-500">
          Cargando hospedajes...
        </div>
      ) : hospedajes.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No hay hospedajes disponibles
        </div>
      ) : (
        <div className="space-y-2">
          {hospedajes.map((hospedaje) => (
            <motion.div
              key={hospedaje._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleSelectHospedaje(hospedaje)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Home className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-semibold">{hospedaje.nombrePropiedad}</div>
                        <div className="text-sm text-gray-500">
                          {hospedaje.numeroCamas} camas • {hospedaje.numeroBanos} baños
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );

  // Renderizar lista de prendas
  const renderPrendas = () => {
    // Filtrar prendas según el término de búsqueda
    const prendasFiltradas = prendas.filter(prenda => {
      if (!searchTerm) return true;
      const nombre = (prenda.nombre || '').toLowerCase();
      const search = searchTerm.toLowerCase();
      return nombre.includes(search);
    });
    
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleVolver}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">Recolectar Prendas</h3>
            <p className="text-sm text-gray-500">
              {selectedHospedaje?.nombrePropiedad || selectedEdificio?.nombre}
            </p>
          </div>
          <Badge variant="secondary">
            <Package className="h-3 w-3 mr-1" />
            {getTotalPrendas()} prendas
          </Badge>
        </div>
        
        {/* Buscador de prendas */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar prenda..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchTerm('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      
        {isLoadingPrendas ? (
          <div className="text-center py-8 text-gray-500">
            Cargando prendas...
          </div>
        ) : prendas.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No hay prendas parametrizadas vertical
          </div>
        ) : prendasFiltradas.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No se encontraron prendas con "{searchTerm}"
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {prendasFiltradas.map((prenda) => {
              const prendaId = prenda.prendaId || prenda._id;
              const tieneCantidad = prendasRecolectadas[prendaId] > 0;
              
              return (
              <Card 
                key={prendaId}
                className={tieneCantidad ? 'border-2 border-green-400 bg-green-50' : ''}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        tieneCantidad ? 'bg-green-100 ring-2 ring-green-400' : 'bg-purple-100'
                      }`}>
                        <Shirt className={`h-5 w-5 ${
                          tieneCantidad ? 'text-green-600' : 'text-purple-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className={`font-medium ${
                          tieneCantidad ? 'text-green-900' : ''
                        }`}>{prenda.nombre}</div>
                        <div className={`text-sm ${
                          tieneCantidad ? 'text-green-700' : 'text-gray-500'
                        }`}>
                          {prenda.peso ? `${prenda.peso}g` : 'Sin peso'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className={`h-8 w-8 ${
                          tieneCantidad ? 'border-green-400 hover:bg-green-100' : ''
                        }`}
                        onClick={() => handleChangeCantidad(prendaId, -1)}
                        disabled={!prendasRecolectadas[prendaId]}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Input
                        type="number"
                        value={prendasRecolectadas[prendaId] || 0}
                        onChange={(e) => handleInputCantidad(prendaId, e.target.value)}
                        className={`w-16 text-center font-semibold ${
                          tieneCantidad ? 'border-green-400 bg-white text-green-900' : ''
                        }`}
                        min="0"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        className={`h-8 w-8 ${
                          tieneCantidad ? 'border-green-400 hover:bg-green-100' : ''
                        }`}
                        onClick={() => handleChangeCantidad(prendaId, 1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>
          
          <div className="flex flex-col gap-2 pt-4 border-t sticky bottom-0 bg-white">
            {/* Botón Cancelar */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleVolver}
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            
            {/* Botón Sin Confirmar - Solo para logística vertical */}
{/*             {selectedEdificio?.tipoLogistica === 'vertical' && (
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => handleConfirmarRecoleccion(true)}
                disabled={getTotalPrendas() === 0}
              >
                <Package className="h-4 w-4 mr-2" />
                Recolectar Sin contar ({getTotalPrendas()})
              </Button>
            )} */}
            
            {/* Botón Confirmar */}
            <Button
              className="w-full"
              onClick={() => handleConfirmarRecoleccion(false)}
              disabled={getTotalPrendas() === 0}
            >
              <Check className="h-4 w-4 mr-2" />
              Confirmar ({getTotalPrendas()})
            </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="p-4">
      <AnimatePresence mode="wait">
        {step === 'edificios' && (
          <motion.div
            key="edificios"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            {renderEdificios()}
          </motion.div>
        )}
        
        {step === 'hospedajes' && (
          <motion.div
            key="hospedajes"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            {renderHospedajes()}
          </motion.div>
        )}
        
        {step === 'prendas' && (
          <motion.div
            key="prendas"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            {renderPrendas()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de Resumen con Firma */}
      <ModalResumenRecoleccion
        isOpen={showResumenModal}
        onClose={() => setShowResumenModal(false)}
        edificio={selectedEdificio}
        hospedaje={selectedHospedaje}
        prendas={prendasParaResumen}
        onConfirm={handleConfirmarConFirma}
      />
    </div>
  );
};

export default RecoleccionRopa;
